export interface Heading {
  level: number;
  text: string;
  /** 1-based line number in the note's Markdown. */
  line: number;
}

const ATX = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/;
const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const SETEXT = /^ {0,3}(=+|-+)[ \t]*$/;
// Lines that cannot be the text of a setext heading (lists, quotes, tables, other headings…).
const NOT_PARAGRAPH = /^ {0,3}([-*+>|#]|\d+[.)]\s|`{3}|~{3})/;

/** Inline Markdown reduced to the words a reader sees in an outline. */
export function plainHeading(text: string): string {
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\\cite\{([^}]*)\}/g, "")
    .replace(/(\*\*|__|\*|_|~~|`)(.+?)\1/g, "$2")
    .replace(/\s+/g, " ")
    .trim();
}

/** The headings of a note in order (ATX "# Title" and underlined setext titles), outside code blocks. */
export function noteHeadings(markdown: string): Heading[] {
  const lines = markdown.split("\n");
  const out: Heading[] = [];
  let fence: string | null = null;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const f = FENCE.exec(line);
    if (fence) {
      if (f && f[1][0] === fence[0] && f[1].length >= fence.length) fence = null;
      continue;
    }
    if (f) {
      fence = f[1];
      continue;
    }
    const atx = ATX.exec(line);
    if (atx) {
      const text = plainHeading(atx[2] ?? "");
      if (text) out.push({ level: atx[1].length, text, line: i + 1 });
      continue;
    }
    const st = SETEXT.exec(line);
    if (st && i > 0) {
      const prev = lines[i - 1];
      const prevIsHeading = out.at(-1)?.line === i;
      if (prev.trim() && !NOT_PARAGRAPH.test(prev) && !prevIsHeading && !(i > 1 && lines[i - 2].trim() && st[1][0] === "-")) {
        const text = plainHeading(prev);
        if (text) out.push({ level: st[1][0] === "=" ? 1 : 2, text, line: i });
      }
    }
  }
  return out;
}
